import i18n from "i18next";
import {DateTime} from "luxon";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {NEGATIVE_COLOR, POSITIVE_COLOR} from "./insightsColors";

export type Direction = -1 | 0 | 1;

export function signed(value: number, format: (value: number) => string): {text: string; direction: Direction} {
    const magnitude = format(Math.abs(value));

    if (magnitude === format(0)) {
        return {text: magnitude, direction: 0};
    }

    return value > 0 ? {text: `+${magnitude}`, direction: 1} : {text: `−${magnitude}`, direction: -1};
}

/** Parses calendar day string ("YYYY-MM-DD") to local timestamp. */
export function calendarDayToTimestamp(day: string): number {
    const [year, month, date] = day.split("-").map(Number);

    return new Date(year, month - 1, date).getTime();
}

/** Luxon [format] in the UI language, so month and weekday names follow it ("08. Okt." in German). */
export const formatLocalDate = (format: string, timestamp: number) =>
    DateTime.fromMillis(timestamp).setLocale(i18n.resolvedLanguage || i18n.language || "en").toFormat(format);

export const formatFullDate = (timestamp: number) => formatLocalDate("ccc, dd.MM.yyyy", timestamp);

/* ---------- Verdicts ---------- */

export function directionColor(direction: Direction, lowerIsBetter = false): string {
    if (direction === 0) return "text.secondary";

    const good = lowerIsBetter ? direction < 0 : direction > 0;
    return good ? POSITIVE_COLOR : NEGATIVE_COLOR;
}

export type Verdict = "neutral" | "positive" | "negative";

export function verdictColor(verdict: Verdict, neutral: string): string;
export function verdictColor(verdict: Verdict): string | undefined;
export function verdictColor(verdict: Verdict, neutral?: string): string | undefined {
    if (verdict === "positive") return POSITIVE_COLOR;
    if (verdict === "negative") return NEGATIVE_COLOR;

    return neutral;
}

/* ---------- Numbers ---------- */

export interface InsightsFormatters {
    count: (value: number) => string;
    whole: (value: number) => string;
    decimal: (value: number) => string;
    /** One decimal even on a round number, for a figure that is a rate rather than a tally. */
    oneDecimal: (value: number) => string;
    percent: (value: number) => string;
    points: (value: number) => string;
}

/*
 * A share under one percent has to keep its decimal: rounded to a whole number it reads as
 * "1 %" or, worse, "0 %" - a funnel stage that did reach somebody stated as one that did not.
 */
const percentText = (
    value: number,
    whole: (value: number) => string,
    decimal: (value: number) => string
): string => {
    const magnitude = Math.abs(value);

    return `${magnitude > 0 && magnitude < 1 ? decimal(value) : whole(value)} %`;
};

export function useInsightsFormatters(): InsightsFormatters {
    const {i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;

    return useMemo(() => {
        const count = new Intl.NumberFormat(locale);
        const whole = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});
        const decimal = new Intl.NumberFormat(locale, {maximumFractionDigits: 1});
        const oneDecimal = new Intl.NumberFormat(locale, {minimumFractionDigits: 1, maximumFractionDigits: 1});

        return {
            count: count.format,
            whole: whole.format,
            decimal: decimal.format,
            oneDecimal: oneDecimal.format,
            percent: (value: number) => percentText(value, whole.format, decimal.format),
            points: whole.format
        };
    }, [locale]);
}
